import {
  APP_INITIALIZER,
  ApplicationConfig,
  provideBrowserGlobalErrorListeners,
  provideZoneChangeDetection,
} from '@angular/core';
import { NavigationError, provideRouter, withNavigationErrorHandler } from '@angular/router';

/**
 * After a deploy, a tab still running the previous build asks for page chunks that no longer exist
 * ("Failed to fetch dynamically imported module"). Load the new build once, at the page it was
 * going to; a session flag stops a reload loop if the chunk is really missing.
 */
export function reloadOnStaleBuild(error: NavigationError): void {
  const message = String((error.error as Error)?.message ?? error.error ?? '');
  if (!/dynamically imported module|Importing a module script failed|ChunkLoadError/i.test(message)) return;
  const key = 'bc_stale_build_reload';
  try {
    if (sessionStorage.getItem(key) === error.url) return;
    sessionStorage.setItem(key, error.url);
  } catch {
    /* storage unavailable: still reload once */
  }
  window.location.assign(error.url);
}

import { routes } from './app.routes';
import {
  HTTP_INTERCEPTORS,
  HttpBackend,
  HttpClient,
  provideHttpClient,
  withInterceptorsFromDi,
} from '@angular/common/http';
import { InteractionType, IPublicClientApplication, PublicClientApplication } from '@azure/msal-browser';
import { msalInstanceConfig } from './core/configs/msalInstanceConfig';
import {
  MSAL_GUARD_CONFIG, MSAL_INSTANCE, MSAL_INTERCEPTOR_CONFIG,
  MsalBroadcastService, MsalGuard,
  MsalGuardConfiguration, MsalInterceptor, MsalInterceptorConfiguration, MsalService,
} from '@azure/msal-angular';
import { MAT_DATE_LOCALE, MatNativeDateModule, provideNativeDateAdapter } from '@angular/material/core';
import { MsalAzureLoginService } from './core/services/msal-azure-login.service';
import { provideAnimations } from '@angular/platform-browser/animations';
import { msalInterceptorConfig } from './core/configs/msalInterceptorConfig';

export function initializeMsal(instance: IPublicClientApplication) {
  return () => instance.initialize();
}

export function MSALInterceptorConfigFactory(): MsalInterceptorConfiguration {
  return {
    interactionType: InteractionType.Redirect,
    protectedResourceMap: msalInterceptorConfig
  };
}

export function MSALInstanceFactory(): IPublicClientApplication {
  return new PublicClientApplication(msalInstanceConfig);
}

export function MSALGuardConfigFactory(): MsalGuardConfiguration {
  return {
    interactionType: InteractionType.Redirect,
    authRequest: {
      scopes: [],
    },
    loginFailedRoute: '/'
  };
}

export let appConfig: ApplicationConfig;
appConfig = {
  providers: [
    provideBrowserGlobalErrorListeners(),
    provideZoneChangeDetection({ eventCoalescing: true }),
    provideRouter(routes, withNavigationErrorHandler(reloadOnStaleBuild)),
    provideHttpClient(withInterceptorsFromDi()),
    provideAnimations(),
    {
      provide: APP_INITIALIZER,
      useFactory: initializeMsal,
      deps: [MSAL_INSTANCE],
      multi: true,
    },
    {
      provide: MSAL_INSTANCE,
      useFactory: MSALInstanceFactory,
    },
    {
      provide: MSAL_GUARD_CONFIG,
      useFactory: MSALGuardConfigFactory,
    },
    {
      provide: HTTP_INTERCEPTORS,
      useClass: MsalInterceptor,
      multi: true,
    },
    {
      provide: MSAL_INTERCEPTOR_CONFIG,
      useFactory: MSALInterceptorConfigFactory,
    },
    {
      // Use this HttpClient For Anonymous Request
      provide: 'NoInterceptorHttpClient',
      useFactory: (handler: HttpBackend) => new HttpClient(handler),
      deps: [HttpBackend],
    },
    {
      provide: MAT_DATE_LOCALE,
      useValue: 'en-GB',
    },
    provideNativeDateAdapter(),
    MatNativeDateModule,
    MsalService,
    MsalGuard,
    MsalBroadcastService,
    MsalAzureLoginService,
  ],
};
